import { useCallback, useRef, useState } from 'react';

const NO_SELECTION = 'NO_SELECTION';

const useConstrainedGridSelection = ({
  getRowConstraintValue,
  disabledRowClassName,
  isEqual = (a, b) => a === b,
}) => {
  const [lockedConstraint, setLockedConstraint] = useState(NO_SELECTION);
  const gridRowsRef = useRef([]);

  const syncGridRows = useCallback((rows) => {
    gridRowsRef.current = rows;
    return rows;
  }, []);

  const getConstraintFromSelectedIds = useCallback((selectedIds) => {
    const rows = gridRowsRef.current;
    const firstRow = rows.find((row) => selectedIds.some(
      (id) => String(id) === String(row.id),
    ));
    if (!firstRow) return undefined;
    return getRowConstraintValue(firstRow);
  }, [getRowConstraintValue]);

  const setLockedConstraintFromSelection = useCallback((selectedIds) => {
    if (selectedIds.length === 0) {
      setLockedConstraint(NO_SELECTION);
    } else if (lockedConstraint === NO_SELECTION) {
      const firstValue = getConstraintFromSelectedIds(selectedIds);
      setLockedConstraint(firstValue);
    }
  }, [lockedConstraint, getConstraintFromSelectedIds]);

  const filterBulkSelection = useCallback((ids) => {
    if (ids.length <= 1) return ids;
    if (lockedConstraint !== NO_SELECTION) return ids;
    const rows = gridRowsRef.current;
    const rowById = new Map(rows.map((r) => [String(r.id), r]));
    const firstSelectedValue = getConstraintFromSelectedIds(ids);
    return ids.filter((id) => {
      const row = rowById.get(String(id));
      if (row) {
        return isEqual(getRowConstraintValue(row), firstSelectedValue);
      }
      return true;
    });
  }, [
    lockedConstraint,
    getConstraintFromSelectedIds,
    getRowConstraintValue,
    isEqual,
  ]);

  const onSelectionChange = useCallback((ids) => {
    const filteredIds = filterBulkSelection(ids);
    setLockedConstraintFromSelection(filteredIds);
    return filteredIds;
  }, [filterBulkSelection, setLockedConstraintFromSelection]);

  const isRowSelectable = useCallback(({ row }) => {
    if (lockedConstraint === NO_SELECTION) return true;
    return isEqual(getRowConstraintValue(row), lockedConstraint);
  }, [lockedConstraint, getRowConstraintValue, isEqual]);

  const getRowClassName = useCallback((row) => {
    if (!isRowSelectable({ row })) return disabledRowClassName;
    return '';
  }, [disabledRowClassName, isRowSelectable]);

  return {
    lockedConstraint,
    syncGridRows,
    onSelectionChange,
    isRowSelectable,
    getRowClassName,
  };
};

export default useConstrainedGridSelection;
